import {computed, ref, watch, type Ref} from 'vue'
import {draftOf, newShot, parseDraft, type ScriptDocument, type ScriptDraft, type ScriptShot, type SketchStroke} from '../types/scripts'

export const SCRIPT_COLUMNS = ['heading', 'visual', 'dialogue', 'shotSize', 'movement', 'seconds', 'notes'] as const
export const SCRIPT_ROWS_MIME = 'application/x-frame-review-script-rows+json'
type Cell = {row: number; col: number}
type CellRange = {r0: number; r1: number; c0: number; c1: number}
type EditableField = (typeof SCRIPT_COLUMNS)[number] | 'location' | 'filmed' | 'imageId'
type Options = {script: Ref<ScriptDocument | null>; workspaceKey: Readonly<Ref<string>>; locked: Readonly<Ref<boolean>>; save: () => Promise<boolean>}
const MAX_BYTES = 2097152, MAX_HISTORY_BYTES = 8 * 1024 * 1024, MAX_HISTORY = 60
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T
const bytes = (value: string) => new TextEncoder().encode(value).byteLength

/** Excel's quoted TSV dialect: tabs, quotes and newlines are preserved in cells. */
export function encodeScriptTSV(rows: string[][]): string {
  return rows.map(row => row.map(cell => /[\t\r\n"]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell).join('\t')).join('\r\n')
}
export function decodeScriptTSV(input: string): string[][] {
  if (bytes(input) > MAX_BYTES) throw new Error('粘贴内容不能超过 2MB')
  input = input.replace(/^\uFEFF/, '')
  const rows: string[][] = [], row: string[] = []
  let cell = '', quoted = false, afterQuote = false, trailingSeparator = false
  const pushCell = () => {row.push(cell); cell = ''; afterQuote = false; if (row.length > SCRIPT_COLUMNS.length) throw new Error('表格最多支持 7 列')}
  const pushRow = () => {pushCell(); rows.push(row.splice(0)); if (rows.length > 200) throw new Error('一次最多粘贴 200 个镜头')}
  for (let index = 0; index < input.length; index++) {
    const char = input[index]
    if (quoted) {
      if (char === '"') {
        if (input[index + 1] === '"') {cell += '"'; index++}
        else {quoted = false; afterQuote = true}
      } else if (char === '\r' && input[index + 1] === '\n') {cell += '\n'; index++}
      else cell += char
      trailingSeparator = false
      continue
    }
    if (afterQuote && char !== '\t' && char !== '\r' && char !== '\n') throw new Error('表格引号格式无效，请重新复制')
    if (char === '\t') {pushCell(); trailingSeparator = false}
    else if (char === '\r' || char === '\n') {pushRow(); if (char === '\r' && input[index + 1] === '\n') index++; trailingSeparator = true}
    else if (char === '"' && cell === '' && !afterQuote) {quoted = true; trailingSeparator = false}
    else {cell += char; trailingSeparator = false}
  }
  if (quoted) throw new Error('表格引号没有闭合，请重新复制')
  if (!trailingSeparator || row.length || cell.length || afterQuote || !rows.length) pushRow()
  const width = Math.max(...rows.map(item => item.length))
  return rows.map(item => [...item, ...Array<string>(width - item.length).fill('')])
}

export function useScriptEditing(options: Options) {
  const {script, workspaceKey, locked} = options
  const error = ref(''), cellSelection = ref<{anchor: Cell; focus: Cell} | null>(null)
  const selectedRowIds = ref<string[]>([]), selectionKind = ref<'cells' | 'rows' | null>(null)
  const editRequest = ref<(Cell & {serial: number}) | null>(null)
  const past = ref<ScriptDraft[]>([]), future = ref<ScriptDraft[]>([])
  let tracked: ScriptDraft | null = null, trackedJSON = '', groupDepth = 0, replaying = false, rowAnchor = 0, requestSerial = 0
  const scope = () => `${workspaceKey.value}\0${script.value?.id || ''}`
  // Server / cached scenes need not have the same property insertion order.
  const canonicalDraft = () => {const draft = script.value ? draftOf(script.value) : null; return draft ? parseDraft(draft) ?? draft : null}
  const source = computed(() => JSON.stringify(canonicalDraft()) || '')
  const cellRange = computed<CellRange | null>(() => {
    if (!cellSelection.value) return null
    const {anchor, focus} = cellSelection.value
    return {r0: Math.min(anchor.row, focus.row), r1: Math.max(anchor.row, focus.row), c0: Math.min(anchor.col, focus.col), c1: Math.max(anchor.col, focus.col)}
  })
  const canUndo = computed(() => !locked.value && !!script.value?.canEdit && (!!past.value.length || (!!tracked && source.value !== trackedJSON)))
  const canRedo = computed(() => !locked.value && !!script.value?.canEdit && future.value.length > 0)
  const selectedIndexes = computed(() => script.value?.scenes.flatMap((shot, index) => selectedRowIds.value.includes(shot.id) ? [index] : []) || [])
  const selectionStamp = () => JSON.stringify({kind: selectionKind.value, cells: cellSelection.value, rows: selectedRowIds.value})
  function trimHistory() {
    while (past.value.length + future.value.length > MAX_HISTORY || [...past.value, ...future.value].reduce((sum, item) => sum + JSON.stringify(item).length * 2, 0) > MAX_HISTORY_BYTES) {
      if (past.value.length) past.value.shift()
      else future.value.shift()
    }
  }
  function clearSelection() {cellSelection.value = null; selectedRowIds.value = []; selectionKind.value = null; editRequest.value = null}
  function resetHistory() {
    past.value = []; future.value = []; groupDepth = 0; error.value = ''; clearSelection()
    tracked = canonicalDraft(); trackedJSON = tracked ? JSON.stringify(tracked) : ''
  }
  watch(() => scope(), resetHistory, {immediate: true, flush: 'sync'})
  function captureChanges() {
    if (replaying || groupDepth || !script.value || source.value === trackedJSON) return
    const next = parseDraft(draftOf(script.value))
    if (!next) return // A focused number field may be temporarily incomplete.
    if (tracked) past.value.push(clone(tracked))
    future.value = []; tracked = next; trackedJSON = JSON.stringify(next); trimHistory()
  }
  watch(source, captureChanges)
  function beginGroup() {if (!groupDepth) captureChanges(); groupDepth++}
  function endGroup() {if (groupDepth) groupDepth--; if (!groupDepth) captureChanges()}
  function finishGroups() {groupDepth = 0; captureChanges()}
  function valid(next: ScriptDraft): ScriptDraft {
    const normalized = parseDraft(next)
    if (!normalized) throw new Error('镜头数据无效：最多 200 镜，请检查文字长度、景别、运镜与整数秒数')
    if (bytes(JSON.stringify({id: script.value?.id, revision: script.value?.revision, ...normalized})) > MAX_BYTES) throw new Error('完整脚本不能超过 2MB，请减少内容后重试')
    return normalized
  }
  function apply(next: ScriptDraft, preserveGroup = false): boolean {
    if (locked.value || !script.value?.canEdit) return false
    try {
      const normalized = valid(next)
      const grouped = preserveGroup && groupDepth > 0
      if (!grouped) finishGroups()
      const before = canonicalDraft()!, serialized = JSON.stringify(normalized)
      if (JSON.stringify(before) === serialized) return true
      if (!grouped) past.value.push(before)
      future.value = []; trimHistory()
      replaying = true; script.value = {...script.value, ...normalized}; replaying = false
      if (!grouped) {tracked = clone(normalized); trackedJSON = serialized}
      error.value = ''; return true
    } catch (cause) {error.value = cause instanceof Error ? cause.message : '无法修改镜头'; return false}
  }
  function edit(operation: (draft: ScriptDraft) => void, preserveGroup = false): boolean {
    if (locked.value || !script.value?.canEdit) return false
    try {const next = draftOf(script.value); operation(next); return apply(next, preserveGroup)}
    catch (cause) {error.value = cause instanceof Error ? cause.message : '无法修改镜头'; return false}
  }
  function replay(direction: 'undo' | 'redo'): boolean {
    if (locked.value || !script.value?.canEdit) return false
    finishGroups()
    const from = direction === 'undo' ? past : future, to = direction === 'undo' ? future : past
    const next = from.value.at(-1)
    if (!next) return false
    const normalized = parseDraft(next)
    if (!normalized) return false
    from.value.pop(); to.value.push(draftOf(script.value))
    replaying = true; script.value = {...script.value, ...clone(normalized)}; replaying = false
    tracked = clone(normalized); trackedJSON = JSON.stringify(normalized); error.value = ''; clearSelection(); trimHistory(); return true
  }
  const undo = () => replay('undo'), redo = () => replay('redo')
  function selectCell(row: number, col: number, extend = false): boolean {
    if (!script.value || row < 0 || row >= script.value.scenes.length || col < 0 || col >= SCRIPT_COLUMNS.length) return false
    const cell = {row, col}
    cellSelection.value = {anchor: extend && cellSelection.value ? cellSelection.value.anchor : cell, focus: cell}
    selectedRowIds.value = []; selectionKind.value = 'cells'; return true
  }
  function selectRow(index: number, config: {extend?: boolean; toggle?: boolean} = {}): boolean {
    if (!script.value || index < 0 || index >= script.value.scenes.length) return false
    const id = script.value.scenes[index].id
    if (config.extend && selectionKind.value === 'rows') selectedRowIds.value = script.value.scenes.slice(Math.min(rowAnchor, index), Math.max(rowAnchor, index) + 1).map(shot => shot.id)
    else if (config.toggle) {selectedRowIds.value = selectedRowIds.value.includes(id) ? selectedRowIds.value.filter(value => value !== id) : [...selectedRowIds.value, id]; rowAnchor = index}
    else {selectedRowIds.value = [id]; rowAnchor = index}
    cellSelection.value = null; selectionKind.value = 'rows'; return true
  }
  function selectAllRows() {if (!script.value) return; selectedRowIds.value = script.value.scenes.map(shot => shot.id); cellSelection.value = null; selectionKind.value = 'rows'; rowAnchor = 0}
  function selectAll() {if (selectionKind.value === 'cells' && script.value?.scenes.length) {selectCell(0, 0); selectCell(script.value.scenes.length - 1, SCRIPT_COLUMNS.length - 1, true)} else selectAllRows()}
  function addShot(index = script.value?.scenes.length ?? 0) {
    return edit(draft => {if (!Number.isInteger(index) || index < 0 || index > draft.scenes.length) throw new Error('插入位置无效'); draft.scenes.splice(index, 0, newShot())})
  }
  function removeRows(ids: string[] = selectedRowIds.value) {
    if (!ids.length) return false
    const ok = edit(draft => {draft.scenes = draft.scenes.filter(shot => !ids.includes(shot.id))})
    if (ok) clearSelection(); return ok
  }
  function reorder(from: number, to: number) {
    return edit(draft => {if (![from, to].every(index => Number.isInteger(index) && index >= 0 && index < draft.scenes.length)) throw new Error('移动位置无效'); const [shot] = draft.scenes.splice(from, 1); draft.scenes.splice(to, 0, shot)})
  }
  function duplicateRows(ids: string[] = selectedRowIds.value) {
    if (!ids.length) return false
    return edit(draft => {draft.scenes = draft.scenes.flatMap(shot => ids.includes(shot.id) ? [shot, {...clone(shot), id: crypto.randomUUID()}] : [shot])})
  }
  function fieldValue(field: EditableField, value: unknown): string | number | boolean {
    if (field === 'seconds') {const n = typeof value === 'string' && !value.trim() ? 0 : Number(value); if (!Number.isInteger(n) || n < 0 || n > 36000) throw new Error('镜头时长须为 0 到 36000 的整数秒'); return n}
    if (field === 'filmed') {if (typeof value !== 'boolean') throw new Error('拍摄状态无效'); return value}
    if (typeof value !== 'string') throw new Error('镜头字段必须是文字')
    return field === 'shotSize' || field === 'movement' ? value.trim() : value
  }
  function setField(shot: ScriptShot, field: EditableField, value: unknown) {Object.assign(shot, {[field]: fieldValue(field, value)})}
  function updateField(row: number, column: number | EditableField, value: unknown) {
    const field = typeof column === 'number' ? SCRIPT_COLUMNS[column] : column
    if (!field || ![...SCRIPT_COLUMNS, 'location', 'filmed', 'imageId'].includes(field)) {error.value = '镜头列无效'; return false}
    return edit(draft => {if (!draft.scenes[row]) throw new Error('镜头不存在'); setField(draft.scenes[row], field, value)}, true)
  }
  function updateSketch(row: number, sketch: SketchStroke[]) {return edit(draft => {if (!draft.scenes[row]) throw new Error('镜头不存在'); draft.scenes[row].sketch = clone(sketch)})}
  function selectedRows(): ScriptShot[] {return script.value?.scenes.filter(shot => selectedRowIds.value.includes(shot.id)).map(clone) || []}
  function clipboardPayload(): {text: string; rich?: string} | null {
    if (!script.value) return null
    if (selectionKind.value === 'rows') {
      const rows = selectedRows(); if (!rows.length) return null
      return {text: encodeScriptTSV(rows.map(shot => SCRIPT_COLUMNS.map(column => String(shot[column])))), rich: JSON.stringify({version: 1, workspace: workspaceKey.value, rows})}
    }
    const range = cellRange.value
    if (!range) return null
    return {text: encodeScriptTSV(script.value.scenes.slice(range.r0, range.r1 + 1).map(shot => SCRIPT_COLUMNS.slice(range.c0, range.c1 + 1).map(column => String(shot[column]))))}
  }
  function clearSelected() {
    if (selectionKind.value === 'rows') return removeRows()
    const range = cellRange.value
    if (!range) return false
    return edit(draft => {for (let row = range.r0; row <= range.r1; row++) for (let col = range.c0; col <= range.c1; col++) setField(draft.scenes[row], SCRIPT_COLUMNS[col], '')})
  }
  function pasteRows(rows: ScriptShot[], index = selectedIndexes.value[0] ?? cellRange.value?.r0 ?? script.value?.scenes.length ?? 0) {
    return edit(draft => {if (!Array.isArray(rows) || !rows.length || !Number.isInteger(index) || index < 0 || index > draft.scenes.length) throw new Error('粘贴镜头位置无效'); draft.scenes.splice(index, 0, ...rows.map(shot => ({...clone(shot), id: crypto.randomUUID()})))})
  }
  function pasteText(text: string, targetRow?: number, targetCol?: number): boolean {
    if (locked.value || !script.value) return false
    try {
      const cells = decodeScriptTSV(text), range = cellRange.value
      const row = targetRow ?? range?.r0 ?? selectedIndexes.value[0] ?? 0, col = targetCol ?? range?.c0 ?? 0
      if (!Number.isInteger(row) || !Number.isInteger(col) || row < 0 || col < 0 || col + cells[0].length > SCRIPT_COLUMNS.length) throw new Error('粘贴内容超出表格列范围')
      const fill = targetRow === undefined && targetCol === undefined && !!range && cells.length === 1 && cells[0].length === 1
      const height = fill ? range!.r1 - row + 1 : cells.length, width = fill ? range!.c1 - col + 1 : cells[0].length
      if (row + height > 200) throw new Error('每个脚本最多 200 个镜头')
      return edit(draft => {
        while (draft.scenes.length < row + height) draft.scenes.push(newShot())
        for (let r = 0; r < height; r++) for (let c = 0; c < width; c++) setField(draft.scenes[row + r], SCRIPT_COLUMNS[col + c], cells[fill ? 0 : r][fill ? 0 : c])
      })
    } catch (cause) {error.value = cause instanceof Error ? cause.message : '表格粘贴失败'; return false}
  }
  function pasteRich(raw: string): boolean {
    try {
      if (bytes(raw) > MAX_BYTES) throw new Error('粘贴镜头不能超过 2MB')
      const payload = JSON.parse(raw)
      if (payload?.version !== 1 || payload.workspace !== workspaceKey.value || !Array.isArray(payload.rows) || !payload.rows.length || payload.rows.length > 200) throw new Error('整行镜头数据无效或来自其他创作空间')
      return pasteRows(payload.rows)
    } catch (cause) {error.value = cause instanceof Error ? cause.message : '整行镜头数据无效'; return false}
  }
  function nativeEditor(target: EventTarget | null) {
    return !!(target as Element | null)?.closest?.('input,textarea,select,[contenteditable]:not([contenteditable="false"])')
  }
  function handleCopy(event: ClipboardEvent): boolean {
    if (nativeEditor(event.target) || !event.clipboardData) return false
    const payload = clipboardPayload(); if (!payload) return false
    event.clipboardData.setData('text/plain', payload.text)
    if (payload.rich) event.clipboardData.setData(SCRIPT_ROWS_MIME, payload.rich)
    event.preventDefault(); return true
  }
  function handleCut(event: ClipboardEvent): boolean {
    if (locked.value || !script.value?.canEdit || !handleCopy(event)) return false
    return clearSelected()
  }
  function handlePaste(event: ClipboardEvent): boolean {
    if (locked.value || !script.value?.canEdit || nativeEditor(event.target) || !event.clipboardData) return false
    const rich = event.clipboardData.getData(SCRIPT_ROWS_MIME), text = event.clipboardData.getData('text/plain')
    if (!rich && !text) return false
    event.preventDefault(); return rich ? pasteRich(rich) : pasteText(text)
  }
  async function writeClipboard(payload: {text: string; rich?: string}) {
    if (!navigator.clipboard) throw new Error('当前浏览器无法读取剪贴板，请使用系统复制粘贴快捷键')
    if (payload.rich && typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
      try {await navigator.clipboard.write([new ClipboardItem({'text/plain': new Blob([payload.text], {type: 'text/plain'}), [`web ${SCRIPT_ROWS_MIME}`]: new Blob([payload.rich], {type: SCRIPT_ROWS_MIME})})]); return}
      catch { /* Safari and older browsers use the explicit TSV fallback. */ }
    }
    await navigator.clipboard.writeText(payload.text)
  }
  async function copySelection(): Promise<boolean> {
    const payload = clipboardPayload(); if (!payload) return false
    try {await writeClipboard(payload); error.value = ''; return true}
    catch (cause) {error.value = cause instanceof Error ? cause.message : '复制失败'; return false}
  }
  async function cutSelection(): Promise<boolean> {
    if (locked.value || !script.value?.canEdit) return false
    const payload = clipboardPayload(), originalScope = scope(), original = source.value, originalSelection = selectionStamp()
    if (!payload) return false
    try {await writeClipboard(payload); if (scope() !== originalScope || source.value !== original || selectionStamp() !== originalSelection || locked.value) throw new Error('内容或选区已变化，仅完成复制，请重新选择后剪切'); return clearSelected()}
    catch (cause) {error.value = cause instanceof Error ? cause.message : '剪切失败'; return false}
  }
  async function pasteFromClipboard(): Promise<boolean> {
    if (locked.value || !script.value?.canEdit) return false
    const originalScope = scope(), original = source.value, originalSelection = selectionStamp()
    const stillCurrent = () => scope() === originalScope && source.value === original && selectionStamp() === originalSelection && !locked.value && !!script.value?.canEdit
    try {
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read()
        if (!stillCurrent()) return false
        for (const item of items) for (const type of [SCRIPT_ROWS_MIME, `web ${SCRIPT_ROWS_MIME}`]) if (item.types.includes(type)) {
          const raw = await (await item.getType(type)).text(); return stillCurrent() ? pasteRich(raw) : false
        }
        for (const item of items) if (item.types.includes('text/plain')) {
          const raw = await (await item.getType('text/plain')).text(); return stillCurrent() ? pasteText(raw) : false
        }
        return false
      }
      const text = await navigator.clipboard.readText()
      if (!stillCurrent()) return false
      return pasteText(text)
    } catch (cause) {error.value = cause instanceof Error ? cause.message : '粘贴失败，请使用系统粘贴快捷键'; return false}
  }
  function requestEdit() {if (locked.value || !script.value?.canEdit || !cellSelection.value) return false; editRequest.value = {...cellSelection.value.focus, serial: ++requestSerial}; return true}
  function handleKeydown(event: KeyboardEvent): boolean {
    if (event.defaultPrevented || event.altKey) return false
    const modifier = event.metaKey || event.ctrlKey, key = event.key.toLowerCase()
    if (modifier && key === 's') {event.preventDefault(); if (!locked.value) void options.save(); return true}
    if (nativeEditor(event.target)) return false
    let handled = false
    if (modifier && key === 'z') handled = event.shiftKey ? redo() : undo()
    else if (modifier && key === 'y') handled = redo()
    else if (modifier && key === 'a') {selectAll(); handled = true}
    else if (!modifier && (key === 'delete' || key === 'backspace')) handled = clearSelected()
    else if (!modifier && (key === 'f2' || key === 'enter')) handled = requestEdit()
    else if (!modifier && ['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'tab'].includes(key) && script.value?.scenes.length) {
      const current = cellSelection.value?.focus ?? {row: 0, col: 0}
      let {row, col} = current
      if (key === 'tab') {const next = Math.max(0, Math.min(script.value.scenes.length * SCRIPT_COLUMNS.length - 1, row * SCRIPT_COLUMNS.length + col + (event.shiftKey ? -1 : 1))); row = Math.floor(next / SCRIPT_COLUMNS.length); col = next % SCRIPT_COLUMNS.length}
      else {row += key === 'arrowup' ? -1 : key === 'arrowdown' ? 1 : 0; col += key === 'arrowleft' ? -1 : key === 'arrowright' ? 1 : 0}
      handled = selectCell(Math.max(0, Math.min(script.value.scenes.length - 1, row)), Math.max(0, Math.min(SCRIPT_COLUMNS.length - 1, col)), key !== 'tab' && event.shiftKey)
    }
    if (handled) event.preventDefault()
    return handled
  }
  const contextMenuActions = computed(() => [
    {id: 'undo', label: '撤销', disabled: !canUndo.value, run: undo},
    {id: 'redo', label: '重做', disabled: !canRedo.value, run: redo},
    {id: 'copy', label: '复制', disabled: !clipboardPayload(), run: copySelection},
    {id: 'cut', label: '剪切', disabled: locked.value || !script.value?.canEdit || !clipboardPayload(), run: cutSelection},
    {id: 'paste', label: '粘贴', disabled: locked.value || !script.value?.canEdit, run: pasteFromClipboard},
    {id: 'select-all', label: '全选', disabled: !script.value?.scenes.length, run: selectAll},
    {id: 'delete', label: '移除 / 清空', disabled: locked.value || !script.value?.canEdit || !clipboardPayload(), run: clearSelected},
  ])
  return {columns: SCRIPT_COLUMNS, error, cellSelection, cellRange, selectedRowIds, selectedIndexes, selectionKind, editRequest, canUndo, canRedo, beginGroup, endGroup, undo, redo, clearSelection, selectCell, selectRow, selectAll, selectAllRows, addShot, removeRows, reorder, duplicateRows, updateField, updateSketch, clipboardPayload, pasteRows, pasteText, copySelection, cutSelection, pasteFromClipboard, handleCopy, handleCut, handlePaste, handleKeydown, contextMenuActions}
}
